import React, {useState, useEffect} from "react";
import personaService from "../../services/personaService";

const PersonaTabView = ({assignmentId}) => {
  const [persona, setPersona] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedFields, setExpandedFields] = useState({});
  const [predatesVersioning, setPredatesVersioning] = useState({});
  const [latestPersonaVersion, setLatestPersonaVersion] = useState({});

  useEffect(() => {
    if (!assignmentId) {
      setLoading(false);
      return;
    }

    async function fetchPersona() {
      try {
        const response = await personaService.getPersonaByAssignmentId(assignmentId);
        setPersona(response.persona);
        setPredatesVersioning(response.predates_versions);
        setLatestPersonaVersion(response.latest_version)

        setLoading(false);
      } catch (err) {
        console.error("Error fetching persona:", err);
        setError("Failed to load persona data");
        setLoading(false);
      }
    }

    fetchPersona();
  }, [assignmentId]);

  const toggleFieldExpansion = (key) => {
    setExpandedFields((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const shouldTruncate = (value) => {
    if (
      typeof value === "object" &&
      value !== null &&
      Object.keys(value).length !== 0
    )
      return true;
    return String(value || "").length > 50;
  };

  const formatValue = (key, value, isExpanded) => {
    if (value === null || value === undefined) return "-";

    let displayValue = value;

    // Handle special case of showing the version.
    if (key === "version") {
      displayValue = predatesVersioning ? "Pre-versioning" : value;
      displayValue += ` (latest: ${latestPersonaVersion})`;
    }

    // Turn strings into JSON
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        if (typeof parsed === "object") {
          displayValue = parsed;
        }
      } catch {
        displayValue = value;
      }
    }

    // Remove the preprocessing value from the models object.
    if (key === "models") {
      delete displayValue?.preprocessing
    }

    if (typeof displayValue === "object") {
      return JSON.stringify(displayValue, null, isExpanded ? 2 : 0);
    }

    const stringValue = String(displayValue);
    if (stringValue.length > 50 && !isExpanded) {
      return `${stringValue.substring(0, 50)}...`;
    }

    return stringValue;
  };

  if (!assignmentId) {
    return <p className="text-gray-400 p-4">No persona data available</p>;
  }

  if (loading) {
    return <p className="text-gray-400 p-4">Loading persona data...</p>;
  }

  if (error) {
    return <p className="text-red-400 p-4">{error}</p>;
  }

  if (!persona) {
    return (
      <p className="text-gray-400 p-4">Persona data could not be loaded</p>
    );
  }

  return (
    <div className="px-4 py-2 space-y-1">
      {Object.entries(persona).map(([key, value], index) => {
        const isExpanded = expandedFields[key] || false;
        const canExpand = shouldTruncate(value);

        return (
          <div key={key} className="group">
            <div className="flex justify-between items-start py-2">
              <div className="font-medium text-gray-300 capitalize w-1/3 text-left pt-1">
                {key.replaceAll("_", " ")}
              </div>
              <div className="text-white w-2/3 text-right break-words">
                <div
                  className={`${
                    isExpanded ? "whitespace-pre-wrap text-left" : "truncate"
                  }`}
                >
                  {formatValue(key, value, isExpanded)}
                </div>
                {canExpand && (
                  <button
                    onClick={() => toggleFieldExpansion(key)}
                    className="text-xs text-blue-400 hover:text-blue-300 mt-1"
                  >
                    {isExpanded ? "Show less" : "Show more"}
                  </button>
                )}
              </div>
            </div>
            {index < Object.entries(persona).length - 1 && (
              <div className="border-b border-gray-700 my-1"></div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PersonaTabView;
